import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  PlayCircle, Timer, Users2, Wallet, ArrowRightCircle, Loader2, Info,
  Sparkles, TrendingUp, Trophy, Rocket, ExternalLink,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => `₦${Number(n || 0).toLocaleString()}`;
const monthLabel = (m) => {
  if (!m) return "";
  const [y, mm] = m.split("-").map(Number);
  return new Date(y, mm - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
};

const medals = ["🥇", "🥈", "🥉"];

/**
 * Public creator-transparency page. Shows the exact commissions math,
 * the last 3 months' unit rates (unit_SBC / unit_DBC / unit_LBC + pool sizes),
 * and a per-genre revenue leaderboard for the most recent closed month.
 * Every share of this URL is a recruiting ad for African filmmakers.
 */
export default function HowPayoutsWork() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "How creators get paid · Christoreel";
    axios.get(`${API_URL}/public/payouts-transparency`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="transparency-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#E65F2B]" />
      </div>
    );
  }

  const { formula, recent_periods, latest_month, genre_leaderboard, lifetime } = data;
  const hasData = recent_periods.length > 0;

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 lg:py-16" data-testid="how-payouts-work-page">
      {/* Hero */}
      <div className="mb-16">
        <p className="text-eyebrow">Transparency · public ledger</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter mt-3">
          How Christoreel <span className="text-[#E65F2B]">pays</span> its creators
        </h1>
        <p className="text-white/60 mt-5 text-base lg:text-lg max-w-3xl leading-relaxed">
          Every naira on this page is real. We publish our monthly unit-rates, pool sizes, and
          per-genre payouts because African filmmakers deserve honest math — not opaque
          &ldquo;watch-time attribution&rdquo; fine print.
        </p>

        {hasData && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8 max-w-3xl" data-testid="hero-lifetime">
            <div className="card-surface p-5">
              <Wallet className="h-5 w-5 text-[#22C55E] mb-2" />
              <p className="text-eyebrow">Paid to creators</p>
              <p className="font-display text-3xl font-black mt-1">{fmt(lifetime.total_paid_ngn)}</p>
              <p className="text-xs text-white/40 mt-1">across {lifetime.closed_months} closed months</p>
            </div>
            <div className="card-surface p-5">
              <Users2 className="h-5 w-5 text-[#60A5FA] mb-2" />
              <p className="text-eyebrow">Creators paid</p>
              <p className="font-display text-3xl font-black mt-1">{lifetime.creators_paid}</p>
            </div>
            <div className="card-surface p-5">
              <TrendingUp className="h-5 w-5 text-[#EAB308] mb-2" />
              <p className="text-eyebrow">Threshold</p>
              <p className="font-display text-3xl font-black mt-1">{fmt(formula.min_payout_ngn)}</p>
              <p className="text-xs text-white/40 mt-1">carries forward until crossed</p>
            </div>
          </div>
        )}
      </div>

      {/* The formula */}
      <section className="mb-16" data-testid="formula-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-[#E65F2B]/10 border border-[#E65F2B]/30 flex items-center justify-center">
            <Sparkles className="h-5 w-5 text-[#E65F2B]" />
          </div>
          <h2 className="font-display text-3xl font-bold">The formula</h2>
        </div>

        <div className="card-surface p-6 sm:p-8 mb-6">
          <p className="text-white/70 text-base leading-relaxed">
            <b className="text-white">{Math.round(formula.creator_share_total * 100)}%</b> of every
            matured monthly subscription (i.e. paid up to <b className="text-white">{formula.maturation_hours} hours</b> ago) is put into the creator pool.
            That pool is split three ways every calendar month:
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="p-5 rounded-xl bg-[#E65F2B]/5 border border-[#E65F2B]/25" data-testid="pool-sbc-card">
              <div className="flex items-baseline justify-between mb-3">
                <PlayCircle className="h-6 w-6 text-[#E65F2B]" />
                <p className="font-display text-3xl font-black text-[#E65F2B]">{Math.round(formula.sbc_share * 100)}%</p>
              </div>
              <p className="font-display text-lg font-bold">SBC — Stream commission</p>
              <p className="text-xs text-white/50 mt-2 leading-relaxed">
                Divided by qualified streams (≥{formula.stream_qualify_seconds}s watch, once per viewer·movie·month).
                Every counted stream gets the same rupee amount.
              </p>
            </div>

            <div className="p-5 rounded-xl bg-[#EAB308]/5 border border-[#EAB308]/25" data-testid="pool-dbc-card">
              <div className="flex items-baseline justify-between mb-3">
                <Timer className="h-6 w-6 text-[#EAB308]" />
                <p className="font-display text-3xl font-black text-[#EAB308]">{Math.round(formula.dbc_share * 100)}%</p>
              </div>
              <p className="font-display text-lg font-bold">DBC — Duration commission</p>
              <p className="text-xs text-white/50 mt-2 leading-relaxed">
                Divided by total minutes watched across the platform. Rewards films that hold
                attention — not just clicks.
              </p>
            </div>

            <div className="p-5 rounded-xl bg-[#60A5FA]/5 border border-[#60A5FA]/25" data-testid="pool-lbc-card">
              <div className="flex items-baseline justify-between mb-3">
                <Users2 className="h-6 w-6 text-[#60A5FA]" />
                <p className="font-display text-3xl font-black text-[#60A5FA]">{Math.round(formula.lbc_share * 100)}%</p>
              </div>
              <p className="font-display text-lg font-bold">LBC — Listener commission</p>
              <p className="text-xs text-white/50 mt-2 leading-relaxed">
                Divided by unique listeners each month. Rewards films that reach fresh
                audiences, not just repeat viewers.
              </p>
            </div>
          </div>

          <div className="mt-6 p-4 rounded-lg bg-white/[0.02] border border-white/5 font-mono text-sm text-white/80" data-testid="formula-equation">
            <span className="text-white/40">Creator earning (per month) =</span><br />
            <span className="pl-4">
              <span className="text-[#E65F2B]">your streams</span> × unit_SBC
              <span className="text-white/30"> + </span>
              <span className="text-[#EAB308]">your minutes</span> × unit_DBC
              <span className="text-white/30"> + </span>
              <span className="text-[#60A5FA]">your listeners</span> × unit_LBC
            </span>
            <br />
            <span className="text-white/40 mt-3 inline-block">Payout ={" "}
              <span className="text-white/80">above ≥ {fmt(formula.min_payout_ngn)}</span> ? auto-transfer to your NUBAN
              : carry forward
            </span>
          </div>
        </div>

        <p className="text-xs text-white/40 max-w-3xl leading-relaxed">
          <Info className="h-3.5 w-3.5 inline mr-1" />
          Nothing behind the paywall. No proprietary &ldquo;recommendation weights&rdquo; secretly tilting
          the math. Every unit-rate below is derived from the same public formula, then locked
          on the second day of the following month.
        </p>
      </section>

      {/* Recent unit rates */}
      <section className="mb-16" data-testid="unit-rates-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-[#EAB308]/10 border border-[#EAB308]/30 flex items-center justify-center">
            <TrendingUp className="h-5 w-5 text-[#EAB308]" />
          </div>
          <h2 className="font-display text-3xl font-bold">Recent unit rates</h2>
        </div>

        {!hasData ? (
          <div className="card-surface p-12 text-center" data-testid="no-closed-periods">
            <p className="text-white/60">
              No closed months yet — Christoreel is in its opening season.
            </p>
            <p className="text-xs text-white/40 mt-2">
              Unit rates get published here on the 2nd day of each month, once the previous month&apos;s subscriptions have matured.
            </p>
          </div>
        ) : (
          <div className="card-surface overflow-hidden" data-testid="unit-rates-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/5 text-left">
                    <th className="px-5 py-4 text-eyebrow">Month</th>
                    <th className="px-5 py-4 text-eyebrow text-right">Matured revenue</th>
                    <th className="px-5 py-4 text-eyebrow text-right">Streams</th>
                    <th className="px-5 py-4 text-eyebrow text-right"><span className="text-[#E65F2B]">Unit SBC</span></th>
                    <th className="px-5 py-4 text-eyebrow text-right"><span className="text-[#EAB308]">Unit DBC / min</span></th>
                    <th className="px-5 py-4 text-eyebrow text-right"><span className="text-[#60A5FA]">Unit LBC</span></th>
                    <th className="px-5 py-4 text-eyebrow text-right">Creators paid</th>
                  </tr>
                </thead>
                <tbody>
                  {recent_periods.map((p) => (
                    <tr key={p.month} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`period-row-${p.month}`}>
                      <td className="px-5 py-4 font-display font-bold">{monthLabel(p.month)}</td>
                      <td className="px-5 py-4 text-right tabular-nums">{fmt(p.matured_paid_up_ngn)}</td>
                      <td className="px-5 py-4 text-right tabular-nums">{p.total_streams.toLocaleString()}</td>
                      <td className="px-5 py-4 text-right tabular-nums font-display font-bold text-[#E65F2B]">{fmt(p.unit_sbc_ngn)}</td>
                      <td className="px-5 py-4 text-right tabular-nums font-display font-bold text-[#EAB308]">{fmt(p.unit_dbc_ngn_per_min)}</td>
                      <td className="px-5 py-4 text-right tabular-nums font-display font-bold text-[#60A5FA]">{fmt(p.unit_lbc_ngn)}</td>
                      <td className="px-5 py-4 text-right tabular-nums">{p.eligible_creator_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {hasData && (
          <p className="text-xs text-white/40 mt-4 max-w-3xl">
            Read the middle three columns as: <b className="text-white/70">&ldquo;a stream is worth {fmt(recent_periods[0].unit_sbc_ngn)},
            a watched minute is worth {fmt(recent_periods[0].unit_dbc_ngn_per_min)}, a unique listener
            is worth {fmt(recent_periods[0].unit_lbc_ngn)}&rdquo;</b> for last month.
          </p>
        )}
      </section>

      {/* Genre leaderboard */}
      <section className="mb-16" data-testid="genre-leaderboard-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-[#22C55E]/10 border border-[#22C55E]/30 flex items-center justify-center">
            <Trophy className="h-5 w-5 text-[#22C55E]" />
          </div>
          <h2 className="font-display text-3xl font-bold">Top-earning genres {latest_month ? `· ${monthLabel(latest_month)}` : ""}</h2>
        </div>

        <p className="text-sm text-white/50 mb-6 max-w-3xl">
          Which stories African audiences are actually paying to watch. Multi-genre films split
          their earnings equally across their tags — so no genre gets double-counted in the total.
        </p>

        {genre_leaderboard.length === 0 ? (
          <div className="card-surface p-12 text-center" data-testid="no-leaderboard">
            <p className="text-white/60">Leaderboard appears once the first month closes.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="genre-leaderboard">
            {genre_leaderboard.map((g, idx) => (
              <Link
                to={`/genres/${g.slug || g.genre.toLowerCase().replace(/\s+/g, "-")}`}
                key={g.genre}
                className="card-surface p-5 flex items-center gap-4 hover:border-[#E65F2B]/30 transition group"
                data-testid={`genre-row-${g.slug || g.genre.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="text-2xl font-display font-black tabular-nums text-white/30 w-8 shrink-0">
                  {idx < 3 ? medals[idx] : `#${idx + 1}`}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-lg font-bold truncate group-hover:text-[#E65F2B] transition">{g.genre}</p>
                  <p className="text-xs text-white/40 mt-0.5">
                    {g.creator_count} creator{g.creator_count === 1 ? "" : "s"} · {g.movie_count} title{g.movie_count === 1 ? "" : "s"} · {g.streams.toLocaleString()} streams
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-display text-xl font-black text-[#EAB308]">{fmt(g.earnings_ngn)}</p>
                  <p className="text-[10px] text-white/40 uppercase tracking-wider">paid</p>
                </div>
                <ExternalLink className="h-4 w-4 text-white/20 group-hover:text-[#E65F2B] transition shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <div className="card-surface p-8 sm:p-12 border-[#E65F2B]/30 relative overflow-hidden" data-testid="cta-card">
        <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#E65F2B]/10 blur-3xl rounded-full pointer-events-none" />
        <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <Rocket className="h-6 w-6 text-[#E65F2B] mb-3" />
            <h2 className="font-display text-2xl sm:text-3xl font-bold">Your story could be next month&apos;s top row.</h2>
            <p className="text-white/60 mt-2 text-sm">
              Register a channel, upload a Christian film, and watch the same public math work
              for you. First payout the moment your monthly balance clears {fmt(formula.min_payout_ngn)}.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="/register" className="ember-btn" data-testid="cta-register">Become a creator</Link>
            <Link to="/submission-guidelines" className="ghost-btn" data-testid="cta-guidelines">Read submission guidelines</Link>
          </div>
        </div>
      </div>

      <p className="text-xs text-white/30 text-center mt-8">
        Numbers are published on the 2nd day of each month and locked after the previous month&apos;s
        subscriptions mature. Auditable · immutable · human-friendly.
      </p>
    </div>
  );
}
